import type { ProgressProjection } from "../../shared/progress";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface ProgressHeaderProps {
  currentStep: number;
  introPosition?: { current: number; total: number };
  onSelectStep: (index: number) => void;
  progress: ProgressProjection;
  steps: Array<{ id: string; title: string }>;
}

export function ProgressHeader({ currentStep, introPosition, onSelectStep, progress, steps }: ProgressHeaderProps) {
  const percentage = progress.totalLayers === 0
    ? 0
    : Math.round((progress.layersComplete / progress.totalLayers) * 100);
  return (
    <div className="walkthrough-progress">
      <Popover>
        <PopoverTrigger render={<Button className="progress-breakdown" size="xs" variant="ghost" />}>
            <strong>{currentStep === 0 ? introPosition ? `Intro ${introPosition.current}/${introPosition.total}` : "Intro" : `${Math.min(currentStep, progress.totalLayers)}/${progress.totalLayers}`}</strong>
            <span>{progress.layersComplete} done</span>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 gap-1 p-1" sideOffset={6}>
          {steps.map((step, index) => {
            const status = progress.layerStatuses[step.id] ?? "unseen";
            return (
              <Button className="h-auto w-full justify-start px-2 py-1.5 text-left" key={step.id} onClick={() => onSelectStep(index)} size="sm" variant="ghost">
                <span className="w-5 text-xs text-muted-foreground">{["done", "question", "problem"].includes(status) ? "✓" : index + 1}</span>
                <span className="min-w-0 flex-1 truncate text-xs">{step.title}</span>
                <span className="text-[10px] capitalize text-muted-foreground">{status.replace("-", " ")}</span>
              </Button>
            );
          })}
        </PopoverContent>
      </Popover>
      <div
        className="progress-track"
        aria-label={`${progress.layersComplete} of ${progress.totalLayers} review parts complete`}
      >
        <div className="progress-fill" style={{ width: `${percentage}%` }} />
        {steps.map((step, index) => {
          const status = progress.layerStatuses[step.id] ?? "unseen";
          const complete = ["done", "question", "problem"].includes(status);
          const current = currentStep === index + 1;
          const left = steps.length === 1 ? 50 : (index / (steps.length - 1)) * 100;
          return (
            <Tooltip key={step.id}>
              <TooltipTrigger render={
                <button
                  aria-label={`${index + 1}. ${step.title}${complete ? ", visited" : ""}`}
                  className={`${complete ? "complete" : ""}${current ? " current" : ""}`}
                  onClick={() => onSelectStep(index)}
                  style={{ left: `${left}%` }}
                  type="button"
                />
              } />
              <TooltipContent side="bottom" sideOffset={5}>{index + 1}. {step.title}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
